import { useState, useCallback, useMemo } from 'react';
import type { ColumnDef, VisibilityState, RowData } from '@tanstack/react-table';
import type { Role } from '@property/shared';
import type { DataTableColumnVisibilityStore } from '@/components/data-table/types.js';
import { useIsTableMobile } from './use-is-table-mobile.js';

interface UseColumnVisibilityOpts<TData extends RowData> {
  tableId: string;
  userId: string | undefined;
  role: Role | undefined;
  columns: ColumnDef<TData>[];
}

interface UseColumnVisibilityResult {
  columnVisibility: VisibilityState;
  setColumnOverride: (columnId: string, visible: boolean) => void;
  resetToDefaults: () => void;
  userOverrides: DataTableColumnVisibilityStore;
}

function storageKey(tableId: string, userId: string | undefined) {
  return `${tableId}:${userId ?? 'anonymous'}`;
}

function readStore(key: string): DataTableColumnVisibilityStore {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as DataTableColumnVisibilityStore) : {};
  } catch {
    return {};
  }
}

export function useColumnVisibility<TData extends RowData>({
  tableId,
  userId,
  role,
  columns,
}: UseColumnVisibilityOpts<TData>): UseColumnVisibilityResult {
  const isMobile = useIsTableMobile();
  const key = storageKey(tableId, userId);

  const [userOverrides, setUserOverrides] = useState<DataTableColumnVisibilityStore>(() =>
    readStore(key),
  );

  const computeDefaults = useCallback((): VisibilityState => {
    const result: VisibilityState = {};
    for (const col of columns) {
      const id =
        (col as { id?: string; accessorKey?: string }).id ??
        (col as { accessorKey?: string }).accessorKey;
      if (!id) continue;
      const meta = col.meta;
      if (meta?.roles && role && !meta.roles.includes(role)) {
        result[id] = false;
        continue;
      }
      if (meta?.hideOnMobile && isMobile) {
        result[id] = false;
        continue;
      }
      if (meta?.defaultHidden) {
        result[id] = false;
        continue;
      }
      result[id] = true;
    }
    return result;
  }, [columns, role, isMobile]);

  const columnVisibility = useMemo((): VisibilityState => {
    const defaults = computeDefaults();
    const merged = { ...defaults, ...userOverrides };
    // mandatory columns are always visible regardless of overrides
    for (const col of columns) {
      const id =
        (col as { id?: string; accessorKey?: string }).id ??
        (col as { accessorKey?: string }).accessorKey;
      if (id && col.meta?.isMandatory) {
        merged[id] = true;
      }
    }
    return merged;
  }, [computeDefaults, userOverrides, columns]);

  const setColumnOverride = useCallback(
    (columnId: string, visible: boolean) => {
      setUserOverrides((prev) => {
        const next = { ...prev, [columnId]: visible };
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // storage quota exceeded — silently ignore
        }
        return next;
      });
    },
    [key],
  );

  const resetToDefaults = useCallback(() => {
    setUserOverrides({});
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  return { columnVisibility, setColumnOverride, resetToDefaults, userOverrides };
}
